import api from "../../services/api";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import logger from "../../utils/logger";
import mergeCollection from "../../utils/helpers/mergeCollection";
import { formatInTimeZone } from 'date-fns-tz';

export default function createBankFileService() {
    // Private data cache
    let data = [];
    let ignoreReasons = []; // Store ignore reasons as an object for quick lookup by ID
    let fileTypes = []; // Store file types for export type changes
    let isListenerRegistered = false;
    let isLoading = false; // Add loading flag to prevent duplicate requests
    let pendingDeleteId = null; // Store the ID of bank file pending deletion
    let isDeleting = false; // Track deletion state
    let currentDateRange = { from: null, to: null }; // Store current date range for export reloads
    let currentShowHistory = false; // Store current history toggle state for export reloads
    let abortController = null; // Track current request for cancellation
    let metadataLoaded = false; // Track if metadata (ignore reasons, file types) has been loaded
    let metadataLoadPromise = null; // Share in-flight metadata requests across bank file refreshes

    function normalizeMetadataList(value) {
        if (Array.isArray(value)) {
            return value;
        }

        if (Array.isArray(value?.data)) {
            return value.data;
        }

        return [];
    }

    function isCanceledResponse(response) {
        return response?.code === 'ERR_CANCELED';
    }

    function loadMetadata() {
        if (metadataLoaded) {
            return Promise.resolve();
        }

        if (!metadataLoadPromise) {
            metadataLoadPromise = Promise.all([
                api.getIgnoreReasons().catch(err => {
                    logger.error('Error loading ignore reasons:', err);
                    return null;
                }),
                api.getFileTypes().catch(err => {
                    logger.error('Error loading file types:', err);
                    return null;
                })
            ])
                .then(([ignoreReasonsResponse, fileTypesResponse]) => {
                    const ignoreReasonsCanceled = isCanceledResponse(ignoreReasonsResponse);
                    const fileTypesCanceled = isCanceledResponse(fileTypesResponse);

                    if (!ignoreReasonsCanceled) {
                        ignoreReasons = normalizeMetadataList(ignoreReasonsResponse);
                        logger.debug('Ignore reasons loaded:', ignoreReasons.length, 'reasons');
                    }

                    if (!fileTypesCanceled) {
                        fileTypes = normalizeMetadataList(fileTypesResponse);
                        logger.debug('File types loaded:', fileTypes.length, 'types');
                    }

                    metadataLoaded = !ignoreReasonsCanceled && !fileTypesCanceled;
                })
                .finally(() => {
                    metadataLoadPromise = null;
                });
        }

        return metadataLoadPromise;
    }

    function getData() {
        return [...data]; // Return a copy to prevent external mutation
    }

    function updateBankFileRecord(updatedData) {
        // Work on a copy to avoid mutation during concurrent reads
        const updated = [...data];
        mergeCollection(updatedData, updated, {
            idField: 'bankFileId',
            event: events.BANKFILE_LOADED
        });
        data = updated;
    }

    function deleteBankFileRecord(id) {
        // Work on a copy to avoid mutation during concurrent reads
        const updated = [...data];
        mergeCollection([{ bankFileId: id }], updated, {
            idField: 'bankFileId',
            hardDelete: true,
            event: events.BANKFILE_LOADED
        });
        data = updated;
    }

    function handleExportDataChanges() {
        logger.debug('Export created, reloading bank files with current settings');
        // Reload bank files with the same date range and history toggle that was active
        if (currentDateRange.from && currentDateRange.to) {
            loadData(true, currentDateRange.from, currentDateRange.to, currentShowHistory)
                .catch((error) => logger.error('Error reloading bank files after export creation:', error));
        }
    }

    function handleExportDeleted() {
        logger.debug('Export deleted, reloading bank files with current settings');
        // Reload bank files with the same date range and history toggle that was active
        if (currentDateRange.from && currentDateRange.to) {
            loadData(true, currentDateRange.from, currentDateRange.to, currentShowHistory)
                .catch((error) => logger.error('Error reloading bank files after export deletion:', error));
        }
    }

    function handleMerchantFileUploaded() {
        logger.debug('Merchant file uploaded, reloading bank files with current settings');
        // Reload bank files with the same date range and history toggle that was active
        if (currentDateRange.from && currentDateRange.to) {
            loadData(true, currentDateRange.from, currentDateRange.to, currentShowHistory)
                .catch((error) => logger.error('Error reloading bank files after merchant file upload:', error));
        }
    }

    function loadData(forceRefresh = false, from, to, showHistory = false) {
        // Store current date range and history state for use by export handlers
        currentDateRange = { from, to };
        currentShowHistory = showHistory;

        // Cancel any ongoing request when starting a new one
        if (abortController) {
            abortController.abort();
            abortController = null;
        }

        // Prevent duplicate requests
        if (isLoading && !forceRefresh) {
            return Promise.resolve([...data]);
        }

        // If data exists and we're not forcing a refresh, return the cached data
        if (data.length > 0 && !forceRefresh) {
            isLoading = false; // Ensure loading state is reset when returning cached data
            Eventbus.emit(events.BANKFILE_LOADED, [...data]);
            return Promise.resolve([...data]);
        }

        // Create new abort controller for this request
        abortController = new AbortController();
        const signal = abortController.signal;
        isLoading = true;
        // Provide default date range if none specified (last 7 days)
        if (!from || !to) {
            const tz = 'Pacific/Auckland';
            const today = new Date();
            const todayNZ = new Date(formatInTimeZone(today, tz, 'yyyy-MM-dd'));
            const sevenDaysAgoNZ = new Date(todayNZ);
            sevenDaysAgoNZ.setDate(todayNZ.getDate() - 6);

            // Format dates in YYYY-MM-DD format using NZ timezone
            to = formatInTimeZone(todayNZ, tz, 'yyyy-MM-dd');
            from = formatInTimeZone(sevenDaysAgoNZ, tz, 'yyyy-MM-dd');

            // Update stored date range with defaults
            currentDateRange = { from, to };

            logger.debug('Using default date range for bank files:', { from, to });
        }

        // Always fetch bank files with abort signal
        const bankFilesPromise = api.getBankFiles(from, to, showHistory, signal);

        // Combine promises - metadata only on first load
        const allPromises = metadataLoaded 
            ? [bankFilesPromise]
            : [bankFilesPromise, loadMetadata()];

        return Promise.all(allPromises)
            .then((responses) => {
                const [bankFilesResponse] = responses;
                
                if (bankFilesResponse?.code === 'ERR_CANCELED') {
                    return data; // Return existing data on cancellation
                }

                data = bankFilesResponse || [];
                logger.debug('Bank files loaded:', data.length, 'records');
                Eventbus.emit(events.BANKFILE_LOADED, [...data]);

                // Only register the event listeners once
                if (!isListenerRegistered) {
                    Eventbus.on(events.SIGNALR.BANKFILE_DATA_CHANGES, updateBankFileRecord);
                    Eventbus.on(events.SIGNALR.BANKFILE_DATA_DELETE, deleteBankFileRecord);
                    Eventbus.on(events.SIGNALR.EXPORT_DATA_CHANGES, handleExportDataChanges);
                    Eventbus.on(events.SIGNALR.EXPORT_DELETED, handleExportDeleted);
                    Eventbus.on(events.SIGNALR.CENTRE_BANKING_MERCHANT_FILE_UPLOADED, handleMerchantFileUploaded);
                    isListenerRegistered = true;
                    logger.debug('SignalR bank file listeners registered');
                }

                return [...data];
            })
            .catch((error) => {
                // Don't log if request was aborted
                if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                    logger.debug('Bank file request was cancelled');
                    return data; // Return existing data on cancellation
                }
                logger.error('Error fetching bank file records or ignore reasons:', error);
                return [];
            })
            .finally(() => {
                isLoading = false;
                abortController = null;
            });
    }

    function init(from, to, showHistory = false) {
        return loadData(true, from, to, showHistory);
    }

    function getBankFile(rowData, pdf = false) {
        if(rowData.id){
            return api.getSupportingDoc(rowData.id);
        }
        return api.getFile(rowData.bankFileId, pdf);
    }

    function clear() {
        // Cancel any ongoing request
        if (abortController) {
            abortController.abort();
            abortController = null;
        }
        if (isListenerRegistered) {
            Eventbus.off(updateBankFileRecord);
            Eventbus.off(deleteBankFileRecord);
            Eventbus.off(handleExportDataChanges);
            Eventbus.off(handleExportDeleted);
            Eventbus.off(handleMerchantFileUploaded);
            isListenerRegistered = false;
            logger.debug('SignalR bank file listeners removed');
        }
        data = [];
        ignoreReasons = []; // Clear ignore reasons
        fileTypes = []; // Clear file types
        pendingDeleteId = null; // Reset delete state
        isDeleting = false;
        currentDateRange = { from: null, to: null }; // Clear date range
        currentShowHistory = false; // Reset history toggle state
        metadataLoaded = false; // Reset metadata loaded flag
        metadataLoadPromise = null;
    }

    function getLoadingState() {
        return isLoading;
    }

    function matchTransactions() {
        return api.autoMatchTransactions()
            .then(() => {
                logger.debug('Transactions matched successfully!');
                Eventbus.emit(events.BANKFILE_MATCHED);
            })
            .catch((error) => {
                logger.error('Error matching transactions:', error);
                throw error; // Propagate the error
            });
    }

    function setPendingDelete(bankFileId) {
        pendingDeleteId = bankFileId;
    }

    function getPendingDelete() {
        return pendingDeleteId;
    }

    function clearPendingDelete() {
        pendingDeleteId = null;
    }

    function getDeletingState() {
        return isDeleting;
    }

    function getIgnoreReasons(){
        if(Array.isArray(ignoreReasons) === false){
            ignoreReasons = [];
        }
        return [...ignoreReasons];
    }

    function getIgnoreReasonById(id) {
        if(Array.isArray(ignoreReasons) === false){
            ignoreReasons = [];
        }
        return ignoreReasons.find(reason => reason.id === id) || null;
    }

    function getFileTypes(){
        if(Array.isArray(fileTypes) === false){
            fileTypes = [];
        }
        return [...fileTypes];
    }

    function getFileTypeById(id) {
        return fileTypes.find(fileType => (fileType.fileTypeId ?? fileType.id) === id) || null;
    }

    function deleteBankFile(id) {
        isDeleting = true;
        return api.deleteFile(id)
            .then((response) => {
                if (response && response.code === 'ERR_CANCELED') {
                    return null; // Return null on cancellation
                }
                logger.debug('Bank file deleted successfully:', response);
                return response; // Return the deletion response
            })
            .catch((error) => {
                logger.error('Error deleting bank file:', error);
                throw error; // Propagate the error
            })
            .finally(() => {
                isDeleting = false;
                clearPendingDelete();
            });
    }

    function updateAmount(payload) {
        return api.updateAmount(payload);
    }

    return {
        getData,
        loadData,
        init,
        getBankFile,
        clear,
        getLoadingState,
        deleteBankFile,
        matchTransactions,
        setPendingDelete,
        getPendingDelete,
        clearPendingDelete,
        getDeletingState,
        getIgnoreReasons,
        getIgnoreReasonById,
        getFileTypes,
        getFileTypeById,
        updateAmount
    };
}
